/**
 * @file Discover Album Folder related helper functions.
 */
import get from 'lodash.get';
import flatten from 'lodash.flatten';

import {
  folderHasSubFolders,
  folderHasSubfoldersByTotals,
  isPrepaidOnlyAlbumFolder,
} from './album-folder';
import { isIdentityJoinedToAlbum } from './identity';
import { ALBUM_PRICE_MODE } from '../constants/albums';
import { PPGD_JOIN_TYPE } from '../constants/discover-album-folder';
import { ALBUM_FOLDER_JOIN_TYPE } from '../constants/album-folder';
import { getAlbumCustomPrintPrice } from './custom-prints';

/**
 * Creates an object for album folder discovery.
 *
 * TODO: A better approach to this -albeit a much bigger task-
 * is to get rid of this method altogether and simply select albums
 * with `album.root_album_folder` as that was the main purpose of it
 * in the beginning (where we didn't have `album.root_album_folder`).
 *
 * @param {Object} album - The album.
 * @param {Object} albumFolder - The album folder currently being onboarded.
 * @returns {Object} The album object with the albumFolder attached.
 */
const getSelectedAlbumData = (album, albumFolder) => {
  return {
    ...album,
    albumFolder,
  };
};

const hasSelectedAlbums = selectedIdentities => {
  return selectedIdentities.some(
    identity => get(identity, 'selectedAlbums.length') > 0,
  );
};

/**
 * Calculates the exact amount of shipping to be paid for a single
 * albumCustomPrint which is selected inside a cart.
 *
 * @param {Object} albumCustomPrint - The selected album custom print.
 * @returns {number}
 */
const getSelectedAlbumCustomPrintShippingPrice = albumCustomPrint => {
  if (!albumCustomPrint) {
    return 0;
  }

  const shippingPrice = get(albumCustomPrint, 'shipping_price.value');
  const shippingEnabled = get(albumCustomPrint, 'shipping_enabled');
  const shippingRequired = get(albumCustomPrint, 'shipping_required');

  if (shippingRequired && shippingEnabled) {
    return shippingPrice || 0;
  }

  if (!shippingRequired && shippingEnabled) {
    return albumCustomPrint.includeShipping ? shippingPrice : 0;
  }

  return 0;
};

/**
 * Determines if a selected album custom print will be shipped or not
 * (also based on user choice of include shipping).
 *
 * @param {Object} albumCustomPrint - The album custom print.
 * @returns {boolean}
 */
const selectedAlbumCustomPrintWillShip = albumCustomPrint => {
  if (!albumCustomPrint) {
    return false;
  }

  const shippingPrice = get(albumCustomPrint, 'shipping_price.value');
  const shippingEnabled = get(albumCustomPrint, 'shipping_enabled');
  const shippingRequired = get(albumCustomPrint, 'shipping_required');

  if (shippingRequired && shippingEnabled && shippingPrice) {
    return true;
  }

  return (
    albumCustomPrint.includeShipping && shippingEnabled && !shippingRequired
  );
};

/**
 * Returns the total sum of the provided album custom prints prices.
 *
 * @param {Array} albumCustomPrints - The album custom prints.
 * @param {boolean} discounted - Whether to return the discounted price or not.
 * @returns {number} - The total sum.
 */
const getAlbumCustomPrintsTotalPrice = (albumCustomPrints = [], discounted) => {
  if (!albumCustomPrints || albumCustomPrints.length === 0) {
    return 0;
  }

  return albumCustomPrints.reduce((sum, albumCustomPrint) => {
    const shippingPrice =
      getSelectedAlbumCustomPrintShippingPrice(albumCustomPrint);

    if (discounted) {
      return (
        sum + shippingPrice + get(albumCustomPrint, 'discount_price.value', 0)
      );
    }

    return sum + shippingPrice + get(albumCustomPrint, 'price.value', 0);
  }, 0);
};

/**
 * Calculates the total owed amount for a single identity for purchasing
 * selected albums based either on PPA-only albums (price at the album level) or
 * returns the folder subscription price (if any), ignoring album level pricing.
 *
 * @param {Object} selectedIdentity - The selected identity.
 *
 * @returns {number} - The total price, either a sum of all PPA-only albums
 * or the folder subscription price if exists.
 */
const getIdentityCheckoutPrice = selectedIdentity => {
  const { selectedAlbums } = selectedIdentity;
  const [firstAlbum] = selectedAlbums;
  const subscriptionAlbumFolder = get(firstAlbum, 'subscription_album_folder');
  const albumCustomPrintsTotalPrice = getAlbumCustomPrintsTotalPrice(
    get(selectedIdentity, 'selectedAlbumCustomPrints', []),
  );

  if (subscriptionAlbumFolder) {
    return subscriptionAlbumFolder.is_demo
      ? 0
      : subscriptionAlbumFolder.price.value + albumCustomPrintsTotalPrice;
  }

  return (
    selectedAlbums.reduce((sum, selectedAlbum) => {
      if (selectedAlbum.is_demo || selectedAlbum.is_free) {
        return sum;
      }

      return sum + (get(selectedAlbum, 'price_whole_album.value') || 0);
    }, 0) + albumCustomPrintsTotalPrice
  );
};

/**
 * Calculates the total owed amount for purchasing selected PPA albums
 * for all selected identities.
 *
 * @param {Array.<Object>} selectedIdentities - The selected identities.
 *
 * @returns {number} - The total price, either a sum of all PPA-only albums
 * or the folder subscription price if exists (for all identities).
 */
const getTotalCheckoutPrice = selectedIdentities => {
  return selectedIdentities
    .filter(
      selectedIdentity => selectedIdentity.joinType === PPGD_JOIN_TYPE.PURCHASE,
    )
    .reduce(
      (sum, selectedIdentity) =>
        sum + getIdentityCheckoutPrice(selectedIdentity),
      0,
    );
};

/**
 * Fetch PPA checkout items for multiple PPA albums or PPA folder subscription.
 *
 * TODO Filter out identities which have previously purchased a folder
 * subscription and return their albums so that we can just join them
 * instead of requiring re-purchasing.
 *
 * @param {Array<Object>} selectedIdentities - User's selected identities.
 * @returns {Object} The target PPA items and Custom Print Items.
 */
const getFolderDiscoveryCheckoutPpaItems = selectedIdentities => {
  if (!selectedIdentities || selectedIdentities.length === 0) {
    return {
      items: [],
      customPrintItems: [],
    };
  }

  const identitiesWithAlbums = selectedIdentities.filter(
    identity => identity.selectedAlbums.length > 0,
  );

  if (!identitiesWithAlbums.length) {
    return {
      items: [],
      customPrintItems: [],
    };
  }

  // The subscription folder (photo pass) should be the same for ALL identities
  // and ALL selected albums since we only allow root level folder
  // subscriptions only.
  const subscriptionAlbumFolder = get(
    identitiesWithAlbums[0].selectedAlbums[0],
    'subscription_album_folder',
  );

  const customPrintItems = flatten(
    identitiesWithAlbums.map(identity =>
      get(identity, 'selectedAlbumCustomPrints', []).map(albumCustomPrint => ({
        album_custom_print_id: albumCustomPrint.id,
        identity_id: identity.id,
        quantity: 1,
        include_shipping: albumCustomPrint.includeShipping,
      })),
    ),
  );

  // Purchasing a folder subscription
  if (subscriptionAlbumFolder) {
    return {
      items: identitiesWithAlbums.map(identity => ({
        identity_id: identity.id,
        album_folder_id: subscriptionAlbumFolder.id,
        join_albums: identity.selectedAlbums.map(album => album.id),
      })),
      customPrintItems,
    };
  }

  // Purchasing multiple individual albums
  return {
    items: identitiesWithAlbums.map(identity => ({
      identity_id: identity.id,
      albums: identity.selectedAlbums.map(album => album.id),
    })),
    customPrintItems,
  };
};

const getAlbumFolderDiscoveryOnboardingItems = (selectedIdentities = []) => {
  const selectedIdentitiesForPurchase = selectedIdentities.filter(
    selectedIdentity => selectedIdentity.joinType === PPGD_JOIN_TYPE.PURCHASE,
  );
  const selectedIdentitiesForJoin = selectedIdentities.filter(
    selectedIdentity => selectedIdentity.joinType === PPGD_JOIN_TYPE.JOIN,
  );
  const { items, customPrintItems } = getFolderDiscoveryCheckoutPpaItems(
    selectedIdentitiesForPurchase,
  );

  // If the AlbumFolder is of type JOIN_ALL, get the AlbumFolder join item.
  const selectedIdentitiesForAlbumFolderJoin = selectedIdentitiesForJoin
    .filter(
      selectedIdentity =>
        selectedIdentity?.selectedAlbums?.length === 0 &&
        selectedIdentity.albumFolders?.[0]?.join_type ===
          ALBUM_FOLDER_JOIN_TYPE.ALL,
    )
    .reduce(
      (albumFolderJoinItem, selectedIdentity) => {
        const [albumFolder] = selectedIdentity.albumFolders;

        return {
          ...albumFolderJoinItem,
          albumFolderId: albumFolder.id,
          join_code: albumFolder.join_code,
          identities: [...albumFolderJoinItem.identities, selectedIdentity.id],
        };
      },
      {
        albumFolderId: null,
        join_code: null,
        identities: [],
      },
    );

  return {
    checkoutPpaItems: items,
    joinItems: selectedIdentitiesForJoin
      .map(selectedIdentity => ({
        identity_id: selectedIdentity.id,
        join_codes: selectedIdentity.selectedAlbums.map(
          album => album.join_code,
        ),
      }))
      .filter(item => item.join_codes.length > 0),
    albumFolderJoinItem: selectedIdentitiesForAlbumFolderJoin,
    customPrintItems,
  };
};

/**
 * Determines whether to display or not an album item
 * in the album folder discovery album selection screen.
 *
 * @param {Object} album - The album to check.
 * @returns {boolean} - Whether or not the album should be displayed.
 */
const shouldDisplayAlbumItem = album =>
  album && !!album.join_code && !!(album.joins_enabled ?? true);

/**
 * Determines whether to display or not an album folder item
 * in the album folder discovery folder selection screen.
 *
 * @param {Object} albumFolder - The album folder to check.
 * @returns {Boolean} - Whether or not the album folder should be displayed.
 */
const shouldDisplayFolderItem = albumFolder =>
  get(albumFolder, 'children.albums.records', []).some(
    shouldDisplayAlbumItem,
  ) ||
  !!(
    folderHasSubFolders(albumFolder) || folderHasSubfoldersByTotals(albumFolder)
  );

/**
 * Determines if an album should be disabled during selection in album folder discovery.
 *
 * @param {Array.<Object>} identity - The identity as returned from GQL containing its memberships.
 * @param {Array.<Object>} albums - All the sibling albums.
 * @param {Array.<Object>} selectedAlbums - Current selected albums of the identity.
 * @param {Object} currentAlbum - The album being rendered.
 *
 * @returns {Boolean} - Whether the album selection should be disabled or not.
 */
const shouldDisableAlbumForIdentity = (
  identity,
  albums,
  selectedAlbums,
  currentAlbum,
) => {
  if (!currentAlbum.is_free) {
    return false;
  }

  const hasSelectedOtherFreeAlbums = selectedAlbums
    .filter(album => album.id !== currentAlbum.id)
    .some(album => album.is_free);

  const isJoinedToOtherFreeAlbums = albums
    .filter(album => album.is_free && album.id !== currentAlbum.id)
    .some(album => isIdentityJoinedToAlbum(identity, album.id));

  return hasSelectedOtherFreeAlbums || isJoinedToOtherFreeAlbums;
};

/**
 * Fetch Entire Album Folder (Gallery) checkout items for multiple Album Folders.
 *
 * TODO Filter out identities which have previously purchased a folder so that we
 * avoid re-purchasing those.
 *
 * @param {Array.<Object>} selectedIdentities - User's selected identities.
 * @returns {*|{ppaItems: Array, customPrintItems: Array}}
 */
const getAlbumFolderCheckoutItems = selectedIdentities => {
  const identitiesWithAlbumFolder = selectedIdentities.filter(
    identity => get(identity, 'albumFolders.length', 0) > 0,
  );

  const base = {
    ppaItems: [],
    customPrintItems: [],
  };

  if (!identitiesWithAlbumFolder.length) {
    return base;
  }

  return (
    identitiesWithAlbumFolder
      // ideally, we'd use == JOIN_TYPE.PURCHASE, but just in case
      // we're missing any backwards compat, using !=== JOIN is better
      .filter(identity => identity.joinType !== PPGD_JOIN_TYPE.JOIN)
      .reduce((items, identity) => {
        const identityPpaItems = identity.albumFolders.map(albumFolder => ({
          identity_id: identity.id,
          album_folder_id: albumFolder.id,
        }));

        const identityCustomPrintItems = get(
          identity,
          'selectedAlbumCustomPrints',
          [],
        ).map(albumCustomPrint => ({
          album_custom_print_id: albumCustomPrint.id,
          identity_id: identity.id,
          quantity: 1,
          include_shipping: albumCustomPrint.includeShipping,
        }));

        return {
          ppaItems: [...items.ppaItems, ...identityPpaItems],
          customPrintItems: [
            ...items.customPrintItems,
            ...identityCustomPrintItems,
          ],
        };
      }, base)
  );
};

/**
 * Calculates the total owed amount for a single identity for purchasing
 * selected album folders and custom prints based either on their price.
 * If no price is provided for the albumFolder, 0 will be assumed.
 *
 * @param {Object} selectedIdentity - The selected identity.
 *
 * @returns {number} - The total price of provided album Folders.
 */
const getAlbumFolderIdentityCheckoutPrice = selectedIdentity => {
  const { albumFolders, selectedAlbumCustomPrints = [] } = selectedIdentity;

  const albumFoldersPrice = albumFolders.reduce((sum, selectedFolder) => {
    return sum + get(selectedFolder, 'price.value', 0);
  }, 0);

  const albumCustomPrintsPrice = selectedAlbumCustomPrints.reduce(
    (sum, albumCustomPrint) => {
      return sum + getAlbumCustomPrintPrice(albumCustomPrint);
    },
    0,
  );

  return albumFoldersPrice + albumCustomPrintsPrice;
};

/**
 * Calculates the total owed amount for purchasing selected Album Folders
 * for selected identities.
 *
 * @param {Array.<Object>} selectedIdentities - The selected identities.
 *
 * @returns {number} - The total price of all identities' selection.
 */
const getAlbumFolderTotalPrice = selectedIdentities => {
  return selectedIdentities
    .filter(identity => identity.joinType !== PPGD_JOIN_TYPE.JOIN)
    .reduce(
      (sum, identity) => sum + getAlbumFolderIdentityCheckoutPrice(identity),
      0,
    );
};

/**
 * Given an album folder and its pricing configuration it
 * determines whether the user should have the option to
 * select whether they are going to purchase or join the items
 * they have selected during onboarding.
 *
 * Refer to the table on the SetAlbumFolderJoinPreferences component for the logic.
 *
 * @param {Object} albumFolder - The Album Folder.
 * @param {ALBUM_PRICE_MODE} albumFolder.album_price_mode - The album folder's price mode.
 * @param {Object} albumFolder.price - The album folder's price.
 * @param {Object} albumFolder.force_join_without_purchase - Whether the user should be force JOINed.
 * @param {Boolean} allowsPrepaidItems - Whether the albumFolder allows for pre-purchase of items
 * @returns {boolean} - Whether the user should be allowed to edit their purchasing/join selection.
 */
const allowsJoinTypeSelection = (albumFolder, allowsPrepaidItems = false) => {
  // Do not allow selection if the album folder forces JOINs.
  // In this case the user must always JOIN without selecting any options
  // even if subscriptions exist.
  if (albumFolder.force_join_without_purchase) {
    return false;
  }

  // Prepaid required gallery
  if (
    isPrepaidOnlyAlbumFolder(albumFolder) &&
    albumFolder.join_type === ALBUM_FOLDER_JOIN_TYPE.ALL
  ) {
    return false;
  }

  // PPD Only
  if (
    !albumFolder.price?.value &&
    albumFolder.album_price_mode === ALBUM_PRICE_MODE.PPD_ONLY
  ) {
    return false;
  }

  // Allows joining without purchasing (i.e. PPA-drip) - the case
  // were we have PPA-only or PPG-only albums/album folder and the user
  // is allowed to join without pre-purchasing the album or album folder.
  const hasPricing =
    albumFolder.price?.value > 0 ||
    albumFolder.album_price_mode !== ALBUM_PRICE_MODE.NONE;

  // Override for explicit prepaid_digital_bundles_enabled false on picture day events.
  if (
    albumFolder.join_without_purchase &&
    albumFolder.picture_day_enabled &&
    hasPricing &&
    albumFolder.join_type !== ALBUM_FOLDER_JOIN_TYPE.ALL &&
    albumFolder.prepaid_digital_bundles_enabled === false
  ) {
    return false;
  }

  if (
    albumFolder.join_without_purchase &&
    albumFolder.join_type !== ALBUM_FOLDER_JOIN_TYPE.ALL &&
    hasPricing
  ) {
    return true;
  }

  // Allows joining without purchasing (i.e. drip) + pe-paid
  // In the case of join-all here, we will be allowing purchase post checkout.
  if (
    albumFolder.join_without_purchase &&
    albumFolder.join_type === ALBUM_FOLDER_JOIN_TYPE.ALL &&
    allowsPrepaidItems
  ) {
    return false;
  }

  // PPG + PPD
  if (
    albumFolder.price?.value > 0 &&
    albumFolder.album_price_mode === ALBUM_PRICE_MODE.PPD_ONLY
  ) {
    return true;
  }

  // NONE + PPA/D
  // But only in the case where `join_type !== ALL` as it doesn't
  // make sense for that flow (user buys/joins album folder and not
  // individual albums).
  if (
    albumFolder.album_price_mode === ALBUM_PRICE_MODE.PPA_AND_PPD &&
    albumFolder.join_type !== ALBUM_FOLDER_JOIN_TYPE.ALL
  ) {
    return true;
  }

  // PPG + PPG-Drip
  // Allows joining without purchasing when PPG + PPG-Drip + Join ALl is available.
  // To be used with care - Join All is expensive enough that we don't
  // want to over-use it, allowing free-joins here may hit the platform
  // harder than we expect.
  if (
    albumFolder.price?.value > 0 &&
    albumFolder.join_without_purchase &&
    albumFolder.join_type === ALBUM_FOLDER_JOIN_TYPE.ALL
  ) {
    return true;
  }

  return false;
};

/**
 * Given an album folder, determine the default join type in the scenario where join type selection is not possible.
 *
 * @param {Object} albumFolder the albumFolder
 * @returns {undefined|string} the default join type
 */
const getDefaultJoinType = albumFolder => {
  if (!albumFolder) {
    return undefined;
  }

  const hasPricing =
    albumFolder.price?.value > 0 ||
    albumFolder.album_price_mode !== ALBUM_PRICE_MODE.NONE;

  if (!hasPricing) {
    return undefined;
  }

  if (!albumFolder.picture_day_enabled) {
    return undefined;
  }

  if (isPrepaidOnlyAlbumFolder(albumFolder)) {
    return undefined;
  }

  if (albumFolder.prepaid_digital_bundles_enabled) {
    return undefined;
  }

  if (!albumFolder.join_without_purchase) {
    return PPGD_JOIN_TYPE.PURCHASE;
  }

  return PPGD_JOIN_TYPE.JOIN;
};

/**
 * Given an album folder determines whether it allows free join or not.
 *
 * @param {Object} albumFolder The folder to check.
 * @returns {boolean} Whether or not the folder allows free join.
 */
const allowsFreeJoin = albumFolder => {
  if (!albumFolder) {
    return false;
  }

  const priceModeAllowsFreeJoin = [ALBUM_PRICE_MODE.PPD_ONLY];

  return (
    priceModeAllowsFreeJoin.includes(albumFolder.album_price_mode) ||
    allowsJoinTypeSelection(albumFolder)
  );
};

/**
 * Gets all selected album custom prints from all identities.
 *
 * @param {Array<Object>} selectedIdentities - The selected identities.
 * @returns {Array} - The album custom prints.
 */
const getAllSelectedAlbumCustomPrints = selectedIdentities => {
  if (!selectedIdentities || selectedIdentities.length === 0) {
    return [];
  }

  return flatten(
    selectedIdentities.map(selectedIdentity =>
      get(selectedIdentity, 'selectedAlbumCustomPrints', []),
    ),
  );
};

/**
 * Determines whether the album folder discovery screens should
 * show the top header brand attribution banner.
 *
 * @param {Object} albumFolder - The album folder.
 * @returns {boolean}
 */
const shouldDisplayBrandAttribution = albumFolder => {
  // Never display attribution banner anymore.
  if (!albumFolder || true) {
    return false;
  }

  return !!albumFolder.account && albumFolder.display_photos_attribution;
};

/**
 * Given a set of selected identities, returns a string of first names
 *
 * @param {Array} selectedIdentities - The selected identities
 * @returns {string}
 */
const getSelectedIdentitiesNameString = (selectedIdentities = []) => {
  const names = selectedIdentities.map(
    identity => identity.first_name || identity.name,
  );

  if (names.length === 1) {
    return names[0];
  }

  if (names.length >= 2) {
    const finalName = names.pop();
    return `${names.join(', ')} and ${finalName}`;
  }

  return '';
};

export {
  getSelectedAlbumData,
  hasSelectedAlbums,
  getIdentityCheckoutPrice,
  getTotalCheckoutPrice,
  getFolderDiscoveryCheckoutPpaItems,
  shouldDisplayFolderItem,
  shouldDisplayAlbumItem,
  shouldDisableAlbumForIdentity,
  getAlbumFolderCheckoutItems,
  getAlbumFolderIdentityCheckoutPrice,
  getAlbumFolderTotalPrice,
  getAlbumFolderDiscoveryOnboardingItems,
  allowsJoinTypeSelection,
  getDefaultJoinType,
  allowsFreeJoin,
  getAllSelectedAlbumCustomPrints,
  getSelectedAlbumCustomPrintShippingPrice,
  getAlbumCustomPrintsTotalPrice,
  shouldDisplayBrandAttribution,
  selectedAlbumCustomPrintWillShip,
  getSelectedIdentitiesNameString,
};
